import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search } from 'lucide-react';
import { useErrorToast, useDismissError } from '../../shared/ErrorToastContext';
import RowActionsMenu from '../../shared/RowActionsMenu';
import '../../shared/ListRowHover.css';
import '../../shared/StyledInput.css';

// Restore-default control — same blue-circle "↺" convention as
// ProductPricesPage.js's manual-price revert icon. Resets the draft, not
// the server — nothing is saved until Save is clicked.
function RevertButton({ onClick, defaultValue, marginTop }) {
  return (
    <span
      onClick={onClick}
      title={defaultValue ? `Reset to default: "${defaultValue}"` : 'Clear'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: '#2563eb',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        cursor: 'pointer',
        flexShrink: 0,
        marginTop,
      }}
    >
      ↺
    </span>
  );
}

const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85', fontWeight: 600 };

function draftFieldStyle(multiline) {
  return {
    flex: 1,
    minWidth: 0,
    boxSizing: 'border-box',
    border: '1px solid #e3e7ee',
    borderRadius: 6,
    padding: '8px 10px',
    fontSize: 14,
    fontFamily: 'inherit',
    color: '#1a1a1a',
    ...(multiline ? { resize: 'vertical' } : {}),
  };
}

// The expanded row's editor — a local draft, only sent to the server on
// Save; Cancel (or navigating away, since the draft lives only in this
// component's state) discards it with nothing written. Replaces the old
// commit-on-blur-per-keystroke behaviour, which had no way to back out of
// an in-progress edit. Saving always writes the *_override columns (the
// code-owned title/tier2_text/article_slug are never touched) — see
// errorCatalogController.updateCatalogEntry.
function ErrorCatalogEditor({ row, onSave, onCancel }) {
  const [titleDraft, setTitleDraft] = useState(row.title_override ?? row.title);
  const [tier2Draft, setTier2Draft] = useState(row.tier2_text_override ?? row.tier2_text);
  const [articleDraft, setArticleDraft] = useState(row.article_slug_override ?? row.article_slug ?? '');
  const [saving, setSaving] = useState(false);

  const titleChanged = titleDraft !== (row.title || '');
  const tier2Changed = tier2Draft !== (row.tier2_text || '');
  const articleChanged = articleDraft !== (row.article_slug || '');
  const previewArticleSlug = articleDraft;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        title_override: titleChanged ? titleDraft : '',
        tier2_text_override: tier2Changed ? tier2Draft : '',
        article_slug_override: articleChanged ? articleDraft : '',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720 }}>
      <div>
        <div style={FIELD_LABEL_STYLE}>Error Title</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <input
            type="text"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            style={draftFieldStyle(false)}
          />
          {titleChanged && <RevertButton onClick={() => setTitleDraft(row.title || '')} defaultValue={row.title} />}
        </div>
      </div>
      <div>
        <div style={FIELD_LABEL_STYLE}>Tier 2 Text</div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 4 }}>
          <textarea
            value={tier2Draft}
            rows={3}
            onChange={(e) => setTier2Draft(e.target.value)}
            style={draftFieldStyle(true)}
          />
          {tier2Changed && <RevertButton onClick={() => setTier2Draft(row.tier2_text || '')} defaultValue={row.tier2_text} marginTop={6} />}
        </div>
      </div>
      <div>
        <div style={FIELD_LABEL_STYLE}>Article</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <input
            type="text"
            value={articleDraft}
            placeholder="no article"
            onChange={(e) => setArticleDraft(e.target.value)}
            style={draftFieldStyle(false)}
          />
          {articleChanged && <RevertButton onClick={() => setArticleDraft(row.article_slug || '')} defaultValue={row.article_slug} />}
        </div>
        {previewArticleSlug && (
          <a
            href={`/global-manual?article=${encodeURIComponent(previewArticleSlug)}`}
            style={{ display: 'inline-block', marginTop: 6, color: '#1976d2', fontSize: 12, textDecoration: 'none' }}
          >
            Manual ↗
          </a>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: saving ? '#90caf9' : '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer' }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: '#ff6f61', color: '#fff', fontWeight: 600, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Reference/settings page (DCN-027 / pat-architecture-error-code-catalog.md)
// — rows are generated from error_catalog (never hand-added here), but
// Error Title, Tier 2 Text, and Article are tenant-editable overrides:
// this is DCN-027's whole point, editing wording (and the Tier 3 link)
// directly without a code change or review. Editing always writes
// title_override/tier2_text_override/article_slug_override, never the
// code-owned title/tier2_text/article_slug columns — sync
// (errorCatalogController.syncErrorCatalog) only ever writes those, never
// the overrides, so an edit here is never silently reverted, and the
// original is always one click away via the ↺ Revert button. An
// article_slug_override is checked against the Manual's actual files
// before saving (backend), so it can't be pointed at a slug that doesn't
// exist. Rollout is touch-driven: an error without a catalog row simply
// doesn't appear, rather than showing as an incomplete row.
//
// The full list is small and bounded (one row per catalog-backed error
// code in the codebase), loaded unpaginated — so search/the Article filter
// are plain client-side filtering rather than a backend query, and rows
// stay collapsed to a single line (ErrID/Title preview/Internal Code) with
// the kebab (⋮) — same convention as a Product row's — opening a row into a
// full-width editor (ErrorCatalogEditor) instead of squeezing Title/Tier 2
// Text into table cells.
function ErrorCatalogPage() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [articleFilter, setArticleFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get('/api/error-catalog')
      .then((res) => setRows(res.data.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const saveEntry = async (row, updates) => {
    try {
      const res = await axios.patch(`/api/error-catalog/${row.id}`, updates);
      setRows((prev) => prev.map((r) => (r.id === row.id ? res.data.data : r)));
      setExpandedId(null);
      dismissError();
    } catch (err) {
      showError(err, 'Could not update this error');
      throw err;
    }
  };

  const visibleRows = rows.filter((row) => {
    const effectiveArticleSlug = row.article_slug_override ?? row.article_slug;
    if (articleFilter === 'linked' && !effectiveArticleSlug) return false;
    if (articleFilter === 'missing' && effectiveArticleSlug) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const title = (row.title_override ?? row.title ?? '').toLowerCase();
    return row.error_code.toLowerCase().includes(q) || row.internal_code.toLowerCase().includes(q) || title.includes(q);
  });

  return (
    <div style={{ margin: '32px 24px 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', paddingBottom: 32 }}>
      <h2 style={{ padding: '20px 24px 4px', margin: 0, fontWeight: 600, fontSize: 20 }}>Error Catalog</h2>
      <p style={{ padding: '0 24px', margin: '0 0 12px', color: '#888', fontSize: 12 }}>
        Every error message shown to users, with its stable code. Click ⋮ then Edit to open an error — Error Title, Tier 2 Text, and Article are all editable there, changes apply once you click Save. Only lists errors that have been migrated to the catalog so far — see DCN-027.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '0 24px 14px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', border: '1px solid #d0d5dd', borderRadius: 8, maxWidth: 280 }}>
          <Search size={14} strokeWidth={1.75} color="#888" />
          <input
            type="text"
            placeholder="Search errors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ border: 'none', outline: 'none', fontSize: 14, width: '100%' }}
          />
        </div>
        <select className="styled-input" value={articleFilter} onChange={(e) => setArticleFilter(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="all">All errors</option>
          <option value="linked">Has article</option>
          <option value="missing">No article</option>
        </select>
      </div>

      <div style={{ margin: '0 24px', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '10%' }} />
            <col style={{ width: '42%' }} />
            <col style={{ width: '28%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>ErrID</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Error Title</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Internal Code</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : visibleRows.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No errors match.</td></tr>
            ) : visibleRows.map((row) => {
              const title = row.title_override ?? row.title;
              const isExpanded = expandedId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className="list-row"
                    onClick={() => setExpandedId(isExpanded ? null : row.id)}
                    style={{ cursor: 'pointer' }}
                    title="Click to edit"
                  >
                    <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{row.error_code}</td>
                    <td style={{ padding: '6px 8px', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={title}>{title}</td>
                    <td style={{ padding: '6px 8px', fontSize: 13, color: '#667085', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.internal_code}>{row.internal_code}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu
                        ariaLabel="Error catalog actions"
                        items={[
                          { key: 'edit', label: isExpanded ? 'Collapse' : 'Edit', onClick: () => setExpandedId(isExpanded ? null : row.id) },
                        ]}
                      />
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={4} style={{ padding: '10px 16px 18px', background: '#f8fafc', borderBottom: '1px solid #e3e7ee' }}>
                        <ErrorCatalogEditor
                          row={row}
                          onSave={(updates) => saveEntry(row, updates)}
                          onCancel={() => setExpandedId(null)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ErrorCatalogPage;
